import { notification } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { UNLOCK_USER } from "utils/api/graphql/mutations/users";
import { USER_LOCK_STATES } from "utils/api/graphql/queries/users";

export const ACCOUNT_STATUSES = {
  ACTIVE: "ACTIVE",
  LOCKED: "LOCKED",
};

const NO_MATCH_ID = "__none__";

const useAccountLock = (where) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.COLLABORATOR);
  const LIST = t("LIST", { returnObjects: true });
  const [status, setStatus] = useState();

  const { data, refetch } = useQuery(USER_LOCK_STATES, {
    variables: { where },
    fetchPolicy: "network-only",
    onError: () => {},
  });

  const lockStates = useMemo(() => {
    const list = data?.userLockStates;
    if (!list) return null;
    return Object.fromEntries(list.map(({ id, locked }) => [id, locked]));
  }, [data]);

  const [unlockUser] = useMutation(UNLOCK_USER, {
    onError: () => notification.error({ message: LIST.UNLOCK_FAILED }),
  });

  const onUnlock = async (id) => {
    const { data: unlocked } = await unlockUser({
      variables: { where: { id } },
    });
    if (!unlocked?.unlockUser) return;
    await refetch();
    notification.success({ message: LIST.UNLOCK_SUCCESS });
  };

  const statusIds = useMemo(() => {
    if (!status || !lockStates) return null;
    const ids = Object.keys(lockStates).filter(
      (id) => lockStates[id] === (status === ACCOUNT_STATUSES.LOCKED),
    );
    return ids.length ? ids : [NO_MATCH_ID];
  }, [status, lockStates]);

  const statusOptions = Object.values(ACCOUNT_STATUSES).map((value) => ({
    value,
    label: LIST.ACCOUNT_STATUS[value],
  }));

  return {
    lockStates,
    onUnlock,
    status,
    setStatus,
    statusIds,
    statusOptions,
  };
};

export default useAccountLock;
